import { Component, OnInit, inject, computed } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { AuthService } from '../../services/auth.service';
import { AppRole } from '../../models/permissions.model';

@Component({
  selector: 'app-unauthorized',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="unauthorized-container">
      <div class="icon-wrapper">
        <i [class]="iconClass" [style.color]="iconColor" style="font-size: 5rem;"></i>
      </div>

      <h1>{{ title }}</h1>
      <p>{{ message }}</p>
      <p class="sub-text" *ngIf="subText">{{ subText }}</p>

      <div class="action-buttons">
        <button (click)="goHome()" class="home-button">Go Back Home</button>
        <button (click)="returnToLogin()" class="login-button">Return to Login</button>
      </div>
    </div>
  `,
  styles: [
    `
      .unauthorized-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 80vh;
        text-align: center;
        padding: 20px;
        animation: fadeIn 0.5s ease-in-out;
      }

      .icon-wrapper {
        margin-bottom: 30px;
        animation: shake 0.5s ease-in-out;
      }

      h1 {
        font-size: 36px;
        margin: 10px 0;
        color: #ff4757;
        font-weight: 700;
      }

      p {
        font-size: 18px;
        color: #555;
        margin: 10px 0;
      }

      .sub-text {
        font-size: 14px;
        color: #888;
        margin-bottom: 30px;
      }

      .action-buttons {
        display: flex;
        gap: 15px;
        flex-wrap: wrap;
        justify-content: center;
      }

      .home-button,
      .login-button {
        padding: 12px 30px;
        text-decoration: none;
        border-radius: 5px;
        font-size: 16px;
        font-weight: 600;
        transition: all 0.3s ease;
        cursor: pointer;
      }

      .home-button {
        background-color: #00008f;
        color: white;
        border: none;
      }

      .home-button:hover {
        background-color: #00006b;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 143, 0.3);
      }

      .login-button {
        background-color: #f1f2f6;
        color: #00008f;
        border: 2px solid #00008f;
      }

      .login-button:hover {
        background-color: #00008f;
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 143, 0.3);
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes shake {
        0%,
        100% {
          transform: translateX(0);
        }
        10%,
        30%,
        50%,
        70%,
        90% {
          transform: translateX(-5px);
        }
        20%,
        40%,
        60%,
        80% {
          transform: translateX(5px);
        }
      }

      @media (max-width: 768px) {
        h1 {
          font-size: 28px;
        }

        p {
          font-size: 16px;
        }

        .home-button,
        .login-button {
          width: 100%;
          max-width: 250px;
        }

        .action-buttons {
          flex-direction: column;
        }
      }
    `,
  ],
})
export class UnauthorizedComponent implements OnInit {
  private router = inject(Router);
  private authService = inject(AuthService);
  private titleService = inject(Title);

  // Dynamic content based on error reason
  iconClass = 'pi pi-lock';
  iconColor = '#ff4757';
  title = 'Access Denied';
  message = "You don't have permission to access this page.";
  subText = 'Please contact your administrator if you believe this is an error.';

  ngOnInit(): void {
    // Check for access denial reason from AuthService
    const denialReason = this.authService.accessDenialReason$.getValue();
    
    if (denialReason) {
      console.log('📄 Unauthorized page - denial reason from AuthService:', denialReason);
      this.iconClass = 'pi pi-exclamation-triangle';
      this.iconColor = '#ffa502';
      this.title = 'Accesso Negato';
      this.message = denialReason;
      this.subText = 'Contatta l\'amministratore per richiedere l\'accesso.';
      this.titleService.setTitle('Accesso Negato - Portale Fiduciari AXA');
      return;
    }
    
    // Read error details from sessionStorage (fallback)
    const errorReason = sessionStorage.getItem('auth_error_reason');
    const errorMessage = sessionStorage.getItem('auth_error_message');

    // Update content based on error type
    if (errorReason === 'insufficient_permissions') {
      this.iconClass = 'pi pi-ban';
      this.iconColor = '#ff6348';
      this.title = 'Insufficient Permissions';
      this.message = errorMessage || "You don't have the required permissions to access this resource.";
      this.subText = 'Your session is still active. Contact your administrator to request access.';
      this.titleService.setTitle('Permessi Insufficienti - Portale Fiduciari AXA');
    } else if (errorReason === 'forbidden') {
      this.iconClass = 'pi pi-times-circle';
      this.iconColor = '#e74c3c';
      this.title = 'Forbidden';
      this.message = errorMessage || 'Access to this resource is explicitly denied.';
      this.subText = 'Please contact your administrator if you believe this is an error.';
      this.titleService.setTitle('Accesso Negato - Portale Fiduciari AXA');
    } else {
      // Default unauthorized (generic, or legacy)
      this.titleService.setTitle('Accesso Negato - Portale Fiduciari AXA');
    }

    // Clear error details after reading (single use)
    if (errorReason) {
      console.log(`📄 Unauthorized page displayed for reason: ${errorReason}`);
      sessionStorage.removeItem('auth_error_reason');
      sessionStorage.removeItem('auth_error_message');
    }
  }

  /**
   * Navigate to home page based on user role
   * - Backoffice users → /axa/dashboard
   * - Professional/Collaborator → /axa/professionals-registration-progress
   * - Other users → /login
   */
  goHome(): void {
    const user = this.authService.getCurrentUser();
    
    if (!user) {
      this.router.navigate(['/login']);
      return;
    }

    // Backoffice users go to dashboard
    const isBackoffice = user.roles.some(role => 
      role === AppRole.SUPER_ADMIN ||
      role === AppRole.BACKOFFICE_ADMIN ||
      role === AppRole.BACKOFFICE_PROFESSIONAL ||
      role === AppRole.BACKOFFICE_SUPPLIER
    );

    if (isBackoffice) {
      console.log('💼 Redirecting backoffice user to dashboard');
      this.router.navigate(['/axa/dashboard']);
      return;
    }

    // Professional/Collaborator go to progression step
    const isProfessionalOrCollaborator = user.roles.some(role => 
      role === AppRole.PROFESSIONAL || 
      role === AppRole.COLLABORATOR
    );

    if (isProfessionalOrCollaborator) {
      console.log('⚖️ Redirecting professional/collaborator to progression step');
      this.router.navigate(['/axa/professionals-registration-progress']);
      return;
    }

    // Default fallback
    this.router.navigate(['/login']);
  }

  /**
   * Logout and redirect to auth-test page for re-authentication
   */
  returnToLogin(): void {
    console.log('🚪 Logging out from unauthorized page...');
    this.authService.logout(); // Already redirects to /auth-test
  }
}
